import React, { useEffect, useState, useRef } from "react";
import { GetDropDownOrders } from "../../services/ClientWaterProfileService";
import CommonLoader from "../common/CommonLoader";

export default function OrderDropdown({
  className,
  isValidated,
  onOrderData, // Callback to pass order data to parent (same as search component)
  accountNumber, // Required prop
  label = "Select Order",
  placeholder = "Select an order...",
  helperText = "Please select a valid order",
}) {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fetch orders when component mounts or accountNumber changes
  useEffect(() => {
    if (!accountNumber) {
      setOrders([]);
      setFilteredOrders([]);
      setSelectedOrder(null);
      setError(null);
      setSearchTerm("");
      onOrderData?.(null);
      isValidated?.(false);
      return;
    }

    const fetchOrders = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const orderData = await GetDropDownOrders(accountNumber);

        console.log("Fetched orders:", orderData); // Debug log

        if (orderData && Array.isArray(orderData) && orderData.length > 0) {
          setOrders(orderData);
          setFilteredOrders(orderData);
        } else if (orderData === 0 || !orderData || orderData.length === 0) {
          setOrders([]);
          setFilteredOrders([]);
          setError("No orders found for this account");
        } else {
          setOrders([]);
          setFilteredOrders([]);
          setError("No orders found for this account");
        }
      } catch (err) {
        console.error("Error fetching orders:", err); // Debug log
        setError("Failed to load orders");
        setOrders([]);
        setFilteredOrders([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [accountNumber]);

  // Filter orders based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOrders(orders);
    } else {
      const filtered = safeFilter(orders, searchTerm);
      setFilteredOrders(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchTerm, orders]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    setIsDropdownOpen(false);
    setSearchTerm("");
    setHighlightedIndex(-1);

    // Pass the order data to parent component (same as search component)
    onOrderData?.(order);
    isValidated?.(true);
  };

  const handleDropdownToggle = () => {
    if (!isLoading && accountNumber) {
      setIsDropdownOpen(!isDropdownOpen);
      if (!isDropdownOpen) {
        // Focus search input when opening dropdown
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      } else {
        setSearchTerm("");
        setHighlightedIndex(-1);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (!isDropdownOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOrders.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOrders.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOrders[highlightedIndex]) {
          handleOrderSelect(filteredOrders[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsDropdownOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
        break;
    }
  };

  const getDropdownBorderColor = () => {
    if (isLoading) return "#3b82f6"; // Blue while loading
    if (selectedOrder) return "#10b981"; // Green for selected order
    if (error) return "#ef4444"; // Red for error
    return "#d1d5db"; // Default gray
  };

  // Fixed highlightText function with null/undefined safety
  const highlightText = (text, highlight) => {
    // Add safety checks for null/undefined text and highlight
    if (!text || !highlight) return text || "";

    // Convert text to string if it's not already
    const textStr = String(text);
    const highlightStr = String(highlight);

    const parts = textStr.split(new RegExp(`(${highlightStr})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === highlightStr.toLowerCase() ? (
        <span
          key={index}
          style={{ backgroundColor: "#fef3c7", fontWeight: "bold" }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // Also update the filter logic to handle null/undefined values
  const safeFilter = (orders, searchTerm) => {
    return orders.filter((order) => {
      const orderNumber = String(order.orderNumber || "");
      const project = String(order.project || "");
      const name = String(order.name || "");

      return (
        orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.toLowerCase().includes(searchTerm.toLowerCase()) ||
        name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  };

  return (
    <div className={`${className || ""}`} ref={dropdownRef}>
      <label
        style={{
          display: "block",
          fontSize: "14px",
          fontWeight: "600",
          color: "#374151",
          marginBottom: "6px",
        }}
      >
        {label}
      </label>

      {/* Dropdown Container */}
      <div style={{ position: "relative" }}>
        <button
          type="button"
          onClick={handleDropdownToggle}
          disabled={isLoading || !accountNumber}
          style={{
            width: "100%",
            padding: "12px 16px",
            fontSize: "16px",
            border: `2px solid ${getDropdownBorderColor()}`,
            borderRadius: "8px",
            outline: "none",
            backgroundColor: "#ffffff",
            textAlign: "left",
            cursor: isLoading || !accountNumber ? "not-allowed" : "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            opacity: isLoading || !accountNumber ? 0.6 : 1,
            transition:
              "border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
            boxShadow:
              isLoading || selectedOrder
                ? `0 0 0 3px ${getDropdownBorderColor()}20`
                : "none",
          }}
        >
          <span style={{ color: selectedOrder ? "#374151" : "#9ca3af" }}>
            {selectedOrder
              ? `${selectedOrder.orderNumber} - ${selectedOrder.project}`
              : placeholder}
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{
              transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
            }}
          >
            <path d="M7 10l5 5 5-5z" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "0",
              right: "0",
              backgroundColor: "#ffffff",
              border: "2px solid #d1d5db",
              borderRadius: "8px",
              marginTop: "4px",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
              zIndex: 1000,
              maxHeight: "350px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Search Input */}
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid #e5e7eb",
                position: "sticky",
                top: 0,
                backgroundColor: "#ffffff",
                zIndex: 1,
              }}
            >
              <div style={{ position: "relative" }}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  style={{
                    width: "100%",
                    padding: "8px 12px 8px 40px",
                    fontSize: "14px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    outline: "none",
                    backgroundColor: "#f9fafb",
                  }}
                />
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#6b7280",
                  }}
                >
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                </svg>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "2px",
                      color: "#6b7280",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Orders List */}
            <div
              style={{
                overflowY: "auto",
                flex: 1,
              }}
            >
              {isLoading ? (
                <div
                  style={{
                    padding: "16px",
                    textAlign: "center",
                    color: "#6b7280",
                  }}
                >
                  Loading orders...
                </div>
              ) : error ? (
                <div
                  style={{
                    padding: "16px",
                    textAlign: "center",
                    color: "#ef4444",
                  }}
                >
                  {error}
                </div>
              ) : filteredOrders.length === 0 ? (
                <div
                  style={{
                    padding: "16px",
                    textAlign: "center",
                    color: "#6b7280",
                  }}
                >
                  {searchTerm
                    ? `No orders found matching "${searchTerm}"`
                    : "No orders available"}
                </div>
              ) : (
                <>
                  {filteredOrders.map((order, index) => {
                    const isSelected =
                      selectedOrder &&
                      selectedOrder.orderNumber === order.orderNumber;
                    const isHighlighted = index === highlightedIndex;
                    return (
                      <div
                        key={order.orderNumber}
                        onClick={() => handleOrderSelect(order)}
                        style={{
                          padding: "12px 16px",
                          cursor: "pointer",
                          backgroundColor: isSelected
                            ? "#e0f2fe"
                            : isHighlighted
                            ? "#f3f4f6"
                            : "#ffffff",
                          borderBottom:
                            index < filteredOrders.length - 1
                              ? "1px solid #e5e7eb"
                              : "none",
                          transition: "background-color 0.2s ease-in-out",
                        }}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "500", color: "#374151" }}>
                            {highlightText(order.orderNumber, searchTerm)}
                          </div>
                          <div style={{ fontSize: "14px", color: "#6b7280" }}>
                            {highlightText(order.project, searchTerm)} -{" "}
                            {highlightText(order.name, searchTerm)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Results count */}
            {!isLoading && !error && orders.length > 0 && (
              <div
                style={{
                  padding: "8px 16px",
                  borderTop: "1px solid #e5e7eb",
                  fontSize: "12px",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  textAlign: "center",
                }}
              >
                {searchTerm
                  ? `${filteredOrders.length} of ${orders.length} orders`
                  : `${orders.length} orders total`}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error message */}
      {error && !isLoading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: "6px",
            color: "#ef4444",
            fontSize: "14px",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{ marginRight: "6px" }}
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          {helperText}
        </div>
      )}

      {/* Success message */}
      {selectedOrder && !isLoading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: "6px",
            color: "#10b981",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{ marginRight: "6px" }}
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
          Order #{selectedOrder.orderNumber} - Selected
        </div>
      )}

      {/* Loading indicator */}
      <CommonLoader loading={isLoading} />

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}
